import { useCallback, useState } from 'react';
import { Carregando, EstadoVazio, Selo } from '../../common/interface';
import { dataDoDia, moeda } from '../formato';
import { useFaturas } from './faturas/useFaturas';
import { papelStatus, faturaEmAberto, rotuloStatus } from './faturas/statusFatura';
import { PixFaturaSheet } from './faturas/PixFaturaSheet';
import { MensalidadesLista } from './faturas/MensalidadesLista';
import type { FaturaCte, PreviaFatura } from './faturas/types';

const periodo = (i: string, f: string) => (i && f ? `${dataDoDia(i)} a ${dataDoDia(f)}` : '—');

/**
 * Aba Faturas — o que o cooperado deve à cooperativa.
 *
 * Duas partes (spec 0105): **Fretes**, as faturas do ICMS dos CT-e, e
 * **Mensalidades**, o que o repasse não descontou. Abre em Fretes, que é o que
 * o cooperado já conhece; a escolha é o mesmo toggle-pill da aba Créditos.
 */
export function FaturasAba() {
  const [parte, setParte] = useState<'fretes' | 'mensalidades'>('fretes');
  const [mensalidadesEmAberto, setMensalidadesEmAberto] = useState<number | null>(null);
  const contarMensalidades = useCallback((n: number) => setMensalidadesEmAberto(n), []);

  return (
    <div>
      <div className="co-cabecalho">
        <div>
          <h1 className="co-titulo-tela">Minhas faturas</h1>
          <p className="co-apoio">
            {parte === 'fretes' ? 'Fretes: fechamento todo dia 1º'
              : mensalidadesEmAberto === null ? 'Mensalidades'
                : `${mensalidadesEmAberto} mensalidade${mensalidadesEmAberto === 1 ? '' : 's'} em aberto`}
          </p>
        </div>
        <div className="toggle-pill" role="radiogroup" aria-label="O que mostrar">
          <input type="radio" id="co-faturas-fretes" name="co-faturas-parte" checked={parte === 'fretes'}
            onChange={() => setParte('fretes')} />
          <label htmlFor="co-faturas-fretes">Fretes</label>
          <input type="radio" id="co-faturas-mensalidades" name="co-faturas-parte" checked={parte === 'mensalidades'}
            onChange={() => setParte('mensalidades')} />
          <label htmlFor="co-faturas-mensalidades">Mensalidades</label>
        </div>
      </div>

      <div className="co-nota-livre">
        {parte === 'fretes' ? <Fretes /> : <MensalidadesLista onTotal={contarMensalidades} />}
      </div>
    </div>
  );
}

/** As faturas de frete: a lista de sempre, sem mudança de forma. */
function Fretes() {
  const { previas, total, carregando, erro, recarregar, porMotorista, temMultiplosMotoristas } = useFaturas();
  const [pagando, setPagando] = useState<FaturaCte | null>(null);

  const previasDe = (id: number) => previas.filter(p => p.idMotorista === id);
  // Prévia de quem ainda não tem fatura nenhuma: sem grupo na lista, ela
  // sumiria — e é justamente o caso de quem começou a emitir neste mês.
  const previasOrfas = previas.filter(p => !porMotorista.has(p.idMotorista));

  return (
    <div>
      {!carregando && !erro && (
        <p className="co-apoio">{`${total} fatura${total === 1 ? '' : 's'} de frete`}</p>
      )}

      {erro && (
        <div className="co-erro" role="alert">
          {erro}
          <div className="co-acoes">
            <button type="button" className="co-btn co-btn-secundario" onClick={recarregar}>Tentar de novo</button>
          </div>
        </div>
      )}

      {carregando && <Carregando rotulo="Carregando as faturas" />}

      {!carregando && !erro && total === 0 && previas.length === 0 && (
        <EstadoVazio titulo="Nenhuma fatura por aqui">
          Assim que você emitir, o valor aparece aqui antes de virar fatura.
        </EstadoVazio>
      )}

      {[...porMotorista.entries()].map(([idMotorista, lista]) => (
        <section key={idMotorista} className="co-grupo-motorista" aria-label={lista[0].nomeMotorista ?? undefined}>
          {temMultiplosMotoristas && (
            <div className="co-motorista">
              <span className="co-conta-iniciais" aria-hidden="true">
                {(lista[0].nomeMotorista ?? '?').trim().slice(0, 2).toUpperCase()}
              </span>
              <div>
                <div className="co-titulo-cartao">{lista[0].nomeMotorista}</div>
                {lista[0].placa && <div className="co-apoio">{lista[0].placa}</div>}
              </div>
            </div>
          )}

          {/* A prévia de quem tem fatura vem logo abaixo do próprio nome: solta,
              o valor apareceria sobre o nome de outra pessoa. */}
          {previasDe(idMotorista).map(p => <CardPrevia key={`p${p.idMotorista}`} previa={p} mostrarNome={false} />)}

          {lista.map(f => (
            <CardFatura
              key={f.id}
              fatura={f}
              mostrarNome={!temMultiplosMotoristas}
              onPagar={() => setPagando(f)}
            />
          ))}
        </section>
      ))}

      {previasOrfas.map(p => <CardPrevia key={`o${p.idMotorista}`} previa={p} mostrarNome />)}

      <PixFaturaSheet
        fatura={pagando}
        onFechar={() => setPagando(null)}
        // Não fecha no desfecho: "pagamento confirmado" e, pior, o recebido que
        // não confere ("não pague de novo") sumiam antes de serem lidos. A
        // lista recarrega por trás.
        onDesfecho={recarregar}
      />
    </div>
  );
}

function CardFatura({ fatura, mostrarNome, onPagar }: {
  fatura: FaturaCte; mostrarNome: boolean; onPagar: () => void;
}) {
  const emAberto = faturaEmAberto(fatura.status);

  return (
    <article className="co-cartao co-fatura">
      <div className="co-fatura-cabeca">
        <div>
          {mostrarNome && fatura.nomeMotorista
            ? <div className="co-titulo-cartao">{fatura.nomeMotorista}</div>
            : <div className="co-apoio">Fatura #{fatura.id}</div>}
          {fatura.placa && mostrarNome && <div className="co-apoio">{fatura.placa}</div>}
        </div>
        <Selo papel={papelStatus(fatura.status)}>{rotuloStatus(fatura.status)}</Selo>
      </div>

      <dl className="co-pares">
        <dt>Período</dt>
        <dd>{periodo(fatura.periodoInicio, fatura.periodoFim)}</dd>
        <dt>Vencimento</dt>
        <dd>{dataDoDia(fatura.dtVencimento)}</dd>
        <dt>Valor</dt>
        <dd className="co-fatura-valor">{moeda(fatura.valor)}</dd>
      </dl>

      {emAberto && (
        <div className="co-acoes">
          <button type="button" className="co-btn co-btn-primario co-btn-largo" onClick={onPagar}>
            {/* Cobrança já gerada muda o rótulo: sem isso o cooperado volta à
                lista, não vê sinal nenhum e gera uma segunda. */}
            {fatura.pix ? 'Ver código Pix' : 'Pagar com Pix'}
          </button>
        </div>
      )}
    </article>
  );
}

/**
 * A prévia do mês corrente.
 *
 * **Sem status, sem número e sem botão** — e não é esquecimento: não existe
 * fatura, logo não há cobrança a criar. O texto também não a chama de próxima
 * fatura: o período é o mês corrente, enquanto a que vence cobra o anterior.
 */
function CardPrevia({ previa, mostrarNome }: { previa: PreviaFatura; mostrarNome: boolean }) {
  return (
    <article className="co-fatura">
      {mostrarNome && <div className="co-titulo-cartao">{previa.nomeMotorista}</div>}

      <p className="co-nota co-nota-sucesso">
        Você já emitiu neste mês. Este valor ainda está sendo somado e só vira fatura no fechamento.
      </p>

      <div className="co-cartao co-nota-livre">
        <span className="co-rotulo">Acumulado até agora</span>
        <div className="co-fatura-grande">{moeda(previa.valorIcms)}</div>
        <dl className="co-pares">
          <dt>Período</dt>
          <dd>{periodo(previa.periodoInicio, previa.periodoFim)}</dd>
          <dt>ATF emitidas</dt>
          <dd>{`${previa.qtdCtes} ${previa.qtdCtes === 1 ? 'emissão' : 'emissões'}`}</dd>
          {/* "Previsto", nunca "Vencimento": a data é projeção do servidor, não
              o vencimento de um documento que exista. */}
          <dt>Fechamento previsto</dt>
          <dd>{dataDoDia(previa.vencimentoEstimado)}</dd>
        </dl>
      </div>
    </article>
  );
}
